<i18n>
{
	"en": {
		"newTripAddress": "Add trip address",
		"deleteTripAddress": "Remove trip address | Remove trip address | Remove trip addresses",
		"noTripAddressesFound": "No TripAddresses...",
		"header": "Trip Addresses ({count})",
		"name": "Name",
		"address": "Street",
		"postalPlace": "Postal Place",
		"postalCode": "Postal Code",
		"countryCode": "Country",
		"size": "Size",
		"editedDate": "Edited Date",
		"updatedTripAddress": "Trip address updated",
		"failedToAdd": "Failed to add new trip address",
		"failedToUpdate": "Failed to update trip address",
		"failedToFetch": "Failed to get trip address data",
		"failedToDelete": "Failed to delete trip address",
		"deletedTripAddresses": "Deleted trip address | Deleted trip address | Deleted {count} trip addresses",
		"terminal": "Terminal",
		"refresh": "Refresh",
		"position":"Position",
		"linkedLocations": "Linked Locations",
		"editLinkedLocations": "Edit Linked Locations"
	},
	"no": {
		"newTripAddress": "Legg til turadresse",
		"deleteTripAddress": "Fjern turadresse | Fjern turadresse | Fjern turadresser",
		"noTripAddressesFound": "Ingen turadresser...",
		"header": "Turadresser ({count})",
		"name": "Navn",
		"address": "Gate",
		"postalPlace": "Sted",
		"postalCode": "Postnummer",
		"countryCode": "Landkode",
		"size": "Størrelse",
		"editedDate": "Endret dato",
		"updatedTripAddress": "Turadresse oppdatert",
		"failedToAdd": "Kunne ikke legge til ny turadresse",
		"failedToUpdate": "Kunne ikke oppdatere turadresse",
		"failedToFetch": "Kunne ikke hente turadresse data",
		"failedToDelete": "Kunne ikke slette turadresse",
		"deletedTripAddresses": "Turadressen ble slettet | Turadressen ble slettet | Slettet {count} turadresser",
		"terminal": "Terminal",
		"refresh": "Refresh",
		"position":"Posisjon",
		"linkedLocations": "Linked Locations",
		"editLinkedLocations": "Edit Linked Locations"
	}
}
</i18n>
<template>
	<t-vertical-panel class="trip-address-list">
		<div
			slot="header"
			class="trip-address-list-header"
		>
			<t-panel-header
				size="md"
				icon="globe"
				type="bp"
			>
				{{ $tc('header', tripAddressesFound) }}
			</t-panel-header>
		</div>

		<div slot="sub-header" class="trip-address-list-sub-header">
			<div class="left">
				<t-button
					:busy="loading"
					iconBefore="refresh"
					appearance="primary"
					@click="fetchAllTripAddresses"
				>
					{{ $t('refresh') }}
				</t-button>
			</div>
			<div class="right">
				<t-button
					:disabled="rowsSelected === 0"
					iconBefore="trash"
					appearance="primary"
					intent="danger"
					type="bp"
					@click="onDeleteTripAddresses"
				>
					{{ $tc('deleteTripAddress', rowsSelected) }}
				</t-button>
				<t-button
					iconBefore="plus"
					appearance="primary"
					@click="onAddTripAddress"
				>
					{{ $t('newTripAddress') }}
				</t-button>
			</div>
		</div>
		<div class="trip-address-list-content">
			<t-grid
				v-if="tripAddressesFound"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="40"
				:items="allTripAddresses"
				:selectableMode="['checkbox']"
				localStorageId="tripAddressesSearchList"
				selectable
				@selected="onRowsSelected"
			>
				<template v-slot:position="{ item }">
					<t-icon-button
						icon="map-marker"
						type="bp"
						appearance="minimal"
						disableBoxShadow
						:disabled="!item.latitude || !item.longitude"
						@click="openPositionInGoogleMaps(item)"
					/>
				</template>
				<template v-slot:name="{ item }">
					<t-text-input
						:value="item.name"
						:isInvalid="errors.name && item.id === errors.id"
						@change="updateValue(item, 'name', $event)"
					/>
				</template>
				<template v-slot:street="{ item }">
					<t-text-input
						:value="item.street"
						:isInvalid="errors.street && item.id === errors.id"
						@change="updateValue(item, 'street', $event)"
					/>
				</template>
				<template v-slot:postalPlace="{ item }">
					<t-text-input
						:value="item.postalPlace"
						:isInvalid="errors.postalPlace && item.id === errors.id"
						@change="updateValue(item, 'postalPlace', $event)"
					/>
				</template>
				<template v-slot:postalCode="{ item }">
					<t-text-input
						:value="item.postalCode"
						:isInvalid="errors.postalCode && item.id === errors.id"
						@change="updateValue(item, 'postalCode', $event)"
					/>
				</template>
				<template v-slot:countryCode="{ item }">
					<t-text-input
						:value="item.countryCode.toUpperCase()"
						:isInvalid="errors.countryCode && item.id === errors.id"
						@change="updateValue(item, 'countryCode', $event)"
					/>
				</template>
				<template v-slot:isTerminal="{ item }">
					<div style="display: flex; flex-direction: row; ">
						<t-checkbox
							style="margin-right: 15px"
							:checked="item.isTerminal"
							name="isPallet"
							@change="updateValue(item, 'isTerminal', $event)"
						/>
					</div>
				</template>

				<template #linkedLocations="{ item, displayValue }">
					<t-tag-container
						:items="displayValue"
						displayKey="name"
						@change="selectLinkedLocations(item, $event)"
					>
						<template v-slot="{ item }">
							{{ item.name }}
						</template>
					</t-tag-container>
				</template>
				<template #options="{ item }">
					<t-icon-button
						:title="$t('editLinkedLocations')"
						icon="annotation"
						type="bp"
						appearance="minimal"
						@click="editLinkedLocations(item.id)"
					/>
				</template>
			</t-grid>
			<t-no-content-panel v-else :text="$t('noTripAddressesFound')" icon="order" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
			<locations-dialog
				:isShown="linkedLocationsShown"
				:existingFilters="selectedLinkedLocations"
				:title="$t('editLinkedLocations')"
				icon="geosearch"
				dialogType="linkedLocations"
				@selected="setLinkedLocations"
				@close="linkedLocationsShown = false"
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import { formatLocationTag } from '@/core';
import { mapState, mapActions } from './store/tripAddresses.store.js';
import LocationsDialog from '@/modules/locations/LocationsDialog';
export default {
	name: 'TripAddresses',
	components: { LocationsDialog },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
			linkedLocationsShown: false,
			selectedTripAddress: null
		};
	},
	computed: {
		...mapState([
			'allTripAddresses',
			'loading',
			'errors',
			'globalLocations'
		]),
		tripAddressesFound() {
			return this.allTripAddresses.length;
		},
		columnDefinitions() {
			return [
				{
					field: 'position',
					displayName: this.$t('position'),
					sortable: false,
					filterable: false,
					width: 70,
				},
				{
					field: 'name',
					displayName: this.$t('name'),
					width: 200,
				},
				{
					field: 'street',
					displayName: this.$t('address'),
					minWidth: 125,
				},
				{
					field: 'postalCode',
					displayName: this.$t('postalCode'),
					minWidth: 125,
				},
				{
					field: 'postalPlace',
					displayName: this.$t('postalPlace'),
					minWidth: 125,
				},
				{
					field: 'countryCode',
					displayName: this.$t('countryCode'),
					width: 80,
				},
				{
					field: 'isTerminal',
					displayName: this.$t('terminal'),
					width: 80,
				},
				{
					field: 'linkedLocations',
					displayName: this.$t('linkedLocations'),
					minWidth: 200,
					sortable: false,
					type: 'array',
					comparableValueGetter: item => this.getLocationNames(item.linkedLocations)
				},
				{
					field: 'options',
					displayName: '',
					sortable: false,
					resizable: false,
					width: 50
				}
			];
		},
		selectedLinkedLocations() {
			if (this.selectedTripAddress && this.selectedTripAddress.linkedLocations) {
				return this.selectedTripAddress.linkedLocations;
			}
			return [];
		}
	},
	async mounted() {
		try {
			await this.fetchAllTripAddresses();
			await this.fetchAllGlobalLocations();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods: {
		...mapActions([
			'fetchAllTripAddresses',
			'setTripAddress',
			'deleteTripAddresses',
			'addEmptyTripAddress',
			'fetchAllGlobalLocations',
			'updateLinkedLocations'
		]),
		addressGetter(party) {
			return `${party.name} ${party.street} ${party.postalPlace}`;
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		async updateValue(tripAddress, prop, data ) {
			try {
				await this.setTripAddress({ tripAddress: tripAddress, data, prop });
				this.$notify({
					type: 'success',
					title: this.$t('updatedTripAddress'),
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdate'),
				});
			}
		},
		async onDeleteTripAddresses() {
			let selectedTripAddresses = this.$refs.grid.getSelectedItems();
			if (!selectedTripAddresses) return;
			try {
				await this.deleteTripAddresses(selectedTripAddresses);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedTripAddresses', selectedTripAddresses.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelete')
				});
			}
		},
		async onAddTripAddress() {
			try {
				await this.addEmptyTripAddress();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToAdd')
				});
			}
		},
		openPositionInGoogleMaps(data) {
			let url = `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`;
			let win = window.open(url, '_blank');
			win.focus();
		},
		editLinkedLocations(id) {
			this.selectedTripAddress = undefined;
			if (id) {
				this.selectedTripAddress = this.allTripAddresses.find( (x) => x.id === id );
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				this.selectedTripAddress = selectedItems[0];
			}
			this.linkedLocationsShown = true;
		},
		getFormattedLinkedLocations(locations) {
			let ret = [];
			if (locations) {
				for (let location of locations) {
					if (this.globalLocations[location]) ret.push(this.globalLocations[location]);
				}
			}
			return ret;
		},
		getLocationNames(locations) {
			let ret = [];
			if (locations) {
				for (let location of locations) {
					ret.push(location.name);
				}
			}
			return ret;
		},
		setLinkedLocations(locations) {
			let newList = locations.map(x => formatLocationTag(x));
			this.selectLinkedLocations(this.selectedTripAddress, newList);
		},
		async selectLinkedLocations(tripAddress, locations) {
			try {
				await this.updateLinkedLocations({ tripAddressId: tripAddress.id, data: locations });
				this.$notify({
					type: 'success',
					title: this.$t('updatedTripAddress'),
				});
			} catch (e) {
				console.log(e);
				this.$notify({
					type: 'error',
					title: this.$t('failedToUpdate'),
				});
			}

			
		},
	},
};
</script>


<style lang="scss" scoped>
.trip-address-list {
	.trip-address-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.trip-address-list-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;
		justify-content: space-between;
		.left {
			display: flex;
			padding: 0px 15px;
			justify-content: flex-start;
			> button {
				margin: 10px;
			}
		}
		.right {
			display: flex;
			padding: 0px 15px;
			justify-content: flex-end;
			> button {
				margin: 10px;
			}
		}
	}
	.trip-address-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
